import type { StBrandToneId, StBudgetFieldId, StConnectionItem, StDoctorItem, StPracticeProfileFieldId, StToggleItem } from '@elva/ui';
import { useLocationStore } from '@elva/platform';
import { growthGet, growthPatchSettingsSection, growthPost, growthPostAccepted } from '@/data/http';
import { autopilotRuleKeyToBe, autopilotRuleKeyToFe } from '@/data/growthEnums';
import { str } from '@/data/adapt';

export interface GrowthSettingsPayload {
  budget: Record<StBudgetFieldId, string>;
  profile: Record<StPracticeProfileFieldId, string>;
  autopilotRules?: Record<string, boolean>;
  notifications?: Record<string, boolean>;
  targetRadiusMiles?: number;
  competitorScanRadiusKm?: number;
  excludedPostalCodes?: string[];
  brandTone?: string;
  spanishContent?: boolean;
  connections?: StConnectionItem[];
  doctors?: StDoctorItem[];
}

export interface GrowthSettingsData {
  settings: GrowthSettingsPayload;
  /** Full practice JSON from GET — merged into PATCH /settings/practice/. */
  rawPractice: Record<string, unknown>;
}

interface BeSettings {
  practice?: Record<string, unknown>;
  practice_profile?: Record<string, unknown>;
  budget?: Record<string, string | number>;
  targeting?: Record<string, string | number | string[]>;
  autopilot?: Record<string, boolean | number>;
  notifications?: Record<string, boolean>;
  content?: Record<string, unknown>;
  doctors?: Array<{
    id?: number | string;
    full_name?: string;
    title?: string;
    specialties?: string[];
  }>;
  connections?: Array<{
    provider?: string;
    status?: string;
    token_expires_at?: string | null;
    last_error?: string | null;
  }>;
}

const BUDGET_KEY_MAP: Record<StBudgetFieldId, string> = {
  'monthly-max': 'monthly_budget_cents',
  'daily-cap': 'daily_cap_cents',
  'campaign-max': 'campaign_max_cents',
  'alert-threshold': 'alert_threshold_cents',
};

/** PATCH /settings/budget/ only accepts these keys (see API contract §A.12). */
const BUDGET_PATCH_UI_KEYS = new Set<StBudgetFieldId>(['monthly-max', 'daily-cap']);

const PROFILE_KEY_MAP: Record<StPracticeProfileFieldId, string> = {
  'practice-name': 'practice_name',
  'phone-number': 'phone_number',
  address: 'address',
  website: 'website',
};

const EMPTY_BUDGET_VALUES: Record<StBudgetFieldId, string> = {
  'monthly-max': '',
  'daily-cap': '',
  'campaign-max': '',
  'alert-threshold': '',
};

const EMPTY_PROFILE_VALUES: Record<StPracticeProfileFieldId, string> = {
  'practice-name': '',
  'phone-number': '',
  address: '',
  website: '',
};

const PROVIDER_META: Record<string, { id: string; title: string }> = {
  google_ads: { id: 'google-ads', title: 'Google Ads' },
  gsc: { id: 'search-console', title: 'Google Search Console' },
  gbp: { id: 'google-business-profile', title: 'Google Business Profile' },
  ga4: { id: 'ga4', title: 'Google Analytics 4' },
};

const AUTOPILOT_RULE_DEFS: StToggleItem[] = [
  {
    key: 'adjust_keyword_bids',
    label: 'Adjust keyword bids',
    description: 'Raise or lower bids based on conversion data to get the best cost per patient.',
    enabled: true,
  },
  {
    key: 'pause_underperforming_keywords',
    label: 'Pause underperforming keywords',
    description: 'Stop spending on keywords that get clicks but no bookings.',
    enabled: true,
  },
  {
    key: 'shift_budget_between_campaigns',
    label: 'Shift budget between campaigns',
    description: 'Move money from lower-performing campaigns to higher-performing ones.',
    enabled: true,
  },
  {
    key: 'ab_test_ad_copy',
    label: 'A/B test ad copy',
    description: 'Create and test new ad headline/description variants.',
    enabled: true,
  },
  {
    key: 'launch_new_campaigns',
    label: 'Launch new campaigns automatically',
    description: 'Let ELVA create and launch new campaigns when it finds strong opportunities.',
    enabled: false,
  },
  {
    key: 'increase_budgets',
    label: 'Increase campaign budgets',
    description: "Let ELVA raise a campaign's budget when it's performing well.",
    enabled: false,
  },
];

const NOTIFICATION_DEFS: Array<{ key: string; label: string; description: string }> = [
  {
    key: 'budget_alerts',
    label: 'Budget alerts',
    description: 'When daily spend exceeds your alert threshold.',
  },
  {
    key: 'weekly_summary',
    label: 'Weekly performance summary',
    description: 'Every Monday: spend, patients booked, and ROI.',
  },
];

function centsToDollars(value: unknown): string | undefined {
  const n = typeof value === 'string' ? Number(value) : (value as number);
  if (!Number.isFinite(n)) return undefined;
  return String(Math.round(n / 100));
}

function dollarsToCents(value: string): number | undefined {
  const n = Number(value.replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n)) return undefined;
  return Math.round(n * 100);
}

function formatTokenExpiry(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const date = new Date(iso);
  if (!Number.isFinite(date.getTime())) return undefined;
  return `Token expires ${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
}

function buildConnectionsList(rows: BeSettings['connections']): StConnectionItem[] {
  const byProvider = new Map(
    (rows ?? []).map((row) => [str(row.provider), adaptConnection(row)]),
  );
  return Object.entries(PROVIDER_META).map(([provider, meta]) => {
    const existing = byProvider.get(provider);
    if (existing) return existing;
    return {
      id: meta.id,
      title: meta.title,
      detail: 'Not connected',
      status: 'not-connected' as const,
      provider,
    };
  });
}

function adaptConnection(row: NonNullable<BeSettings['connections']>[number]): StConnectionItem {
  const provider = str(row.provider);
  const meta = PROVIDER_META[provider] ?? {
    id: provider.replace(/_/g, '-'),
    title: provider.replace(/_/g, ' '),
  };
  const wireStatus = str(row.status).toLowerCase();
  const status: StConnectionItem['status'] = wireStatus === 'connected' ? 'connected' : 'not-connected';
  const detail =
    str(row.last_error) ||
    formatTokenExpiry(row.token_expires_at) ||
    (status === 'connected' ? 'Connected' : wireStatus === 'expired' ? 'Connection expired — reconnect' : 'Not connected');

  return {
    id: meta.id,
    title: meta.title,
    detail,
    status,
    provider,
  };
}

function adaptDoctors(rows: BeSettings['doctors']): StDoctorItem[] {
  return (rows ?? []).map((row) => ({
    id: String(row.id ?? row.full_name),
    name: str(row.full_name),
    roleLine: [row.title, ...(row.specialties ?? [])].filter(Boolean).join(' · ') || '—',
  }));
}

export function buildAutopilotToggles(source: Record<string, boolean | undefined>): StToggleItem[] {
  return AUTOPILOT_RULE_DEFS.map((rule) => ({
    ...rule,
    enabled: source[rule.key!] ?? rule.enabled,
  }));
}

export function buildNotificationToggles(source: Record<string, boolean | undefined>): StToggleItem[] {
  return NOTIFICATION_DEFS.map((rule) => ({
    key: rule.key,
    label: rule.label,
    description: rule.description,
    enabled: Boolean(source[rule.key]),
  }));
}

export function adaptSettings(be: BeSettings): GrowthSettingsData {
  const practiceSource = (be.practice ?? be.practice_profile ?? {}) as Record<string, unknown>;
  const budgetSource = be.budget ?? {};

  const budget = { ...EMPTY_BUDGET_VALUES };
  for (const [uiKey, beKey] of Object.entries(BUDGET_KEY_MAP)) {
    const dollars = centsToDollars(budgetSource[beKey]);
    if (dollars != null) budget[uiKey as StBudgetFieldId] = dollars;
  }

  const profile = { ...EMPTY_PROFILE_VALUES };
  for (const [uiKey, beKey] of Object.entries(PROFILE_KEY_MAP)) {
    const val = practiceSource[beKey];
    if (val != null) profile[uiKey as StPracticeProfileFieldId] = String(val);
  }

  const autopilotSource = be.autopilot ?? {};
  const autopilotRules: Record<string, boolean> = {};
  for (const [beKey, value] of Object.entries(autopilotSource)) {
    if (typeof value === 'boolean') {
      autopilotRules[autopilotRuleKeyToFe(beKey)] = value;
      autopilotRules[beKey] = value;
    }
  }

  const targeting = be.targeting ?? {};
  const adRadius = targeting.ad_radius_miles ?? targeting.service_radius_km;
  const excluded =
    (targeting.excluded_postal_codes as string[] | undefined) ??
    (targeting.excluded_zips as string[] | undefined) ??
    [];

  const content = be.content ?? {};
  const brandTone = str(content.brand_tone, '') || undefined;
  const spanishContent =
    typeof content.spanish_content === 'boolean' ? content.spanish_content : undefined;

  const settings: GrowthSettingsPayload = {
    budget,
    profile,
    ...(Object.keys(autopilotRules).length ? { autopilotRules } : {}),
    ...(be.notifications ? { notifications: be.notifications } : {}),
    ...(adRadius != null ? { targetRadiusMiles: Number(adRadius) } : {}),
    ...(targeting.competitor_scan_radius_km != null
      ? { competitorScanRadiusKm: Number(targeting.competitor_scan_radius_km) }
      : {}),
    ...(excluded.length ? { excludedPostalCodes: excluded.map(String) } : {}),
    ...(brandTone ? { brandTone } : {}),
    ...(spanishContent != null ? { spanishContent } : {}),
    connections: buildConnectionsList(be.connections),
    ...(be.doctors?.length ? { doctors: adaptDoctors(be.doctors) } : {}),
  };

  return { settings, rawPractice: { ...practiceSource } };
}

function autopilotRulesToBe(rules: Record<string, boolean>): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const [key, enabled] of Object.entries(rules)) {
    out[autopilotRuleKeyToBe(key)] = enabled;
  }
  return out;
}

function stripLocationId(body: Record<string, unknown>): Record<string, unknown> {
  const { location_id: _locationId, ...rest } = body;
  return rest;
}

export function settingsToPatchSections(
  payload: Partial<GrowthSettingsPayload>,
  rawPractice: Record<string, unknown>,
): Array<{ section: string; body: Record<string, unknown> }> {
  const patches: Array<{ section: string; body: Record<string, unknown> }> = [];

  if (payload.budget) {
    const body: Record<string, unknown> = {};
    for (const [uiKey, beKey] of Object.entries(BUDGET_KEY_MAP)) {
      if (!BUDGET_PATCH_UI_KEYS.has(uiKey as StBudgetFieldId)) continue;
      const val = payload.budget[uiKey as StBudgetFieldId];
      const cents = val != null ? dollarsToCents(val) : undefined;
      if (cents != null) body[beKey] = cents;
    }
    if (Object.keys(body).length) patches.push({ section: 'budget', body: stripLocationId(body) });
  }

  if (payload.profile) {
    const practice = stripLocationId({ ...rawPractice });
    for (const [uiKey, beKey] of Object.entries(PROFILE_KEY_MAP)) {
      const val = payload.profile[uiKey as StPracticeProfileFieldId];
      if (val != null) practice[beKey] = val;
    }
    patches.push({ section: 'practice', body: practice });
  }

  if (payload.autopilotRules) {
    patches.push({
      section: 'autopilot',
      body: stripLocationId(autopilotRulesToBe(payload.autopilotRules)),
    });
  }

  if (payload.notifications) {
    patches.push({ section: 'notifications', body: stripLocationId(payload.notifications) });
  }

  if (
    payload.targetRadiusMiles != null ||
    payload.competitorScanRadiusKm != null ||
    payload.excludedPostalCodes
  ) {
    const body: Record<string, unknown> = {};
    if (payload.targetRadiusMiles != null) body.ad_radius_miles = payload.targetRadiusMiles;
    if (payload.competitorScanRadiusKm != null) {
      body.competitor_scan_radius_km = payload.competitorScanRadiusKm;
    }
    if (payload.excludedPostalCodes) {
      body.excluded_postal_codes = payload.excludedPostalCodes;
    }
    patches.push({ section: 'targeting', body: stripLocationId(body) });
  }

  if (payload.brandTone != null || payload.spanishContent != null) {
    const body: Record<string, unknown> = {};
    if (payload.brandTone != null) body.brand_tone = payload.brandTone;
    if (payload.spanishContent != null) body.spanish_content = payload.spanishContent;
    patches.push({ section: 'content', body: stripLocationId(body) });
  }

  return patches;
}

export async function fetchGrowthSettings(): Promise<GrowthSettingsData> {
  const be = await growthGet<BeSettings>('/settings/');
  return adaptSettings(be);
}

export async function patchGrowthSettings(
  payload: Partial<GrowthSettingsPayload>,
  rawPractice: Record<string, unknown>,
): Promise<GrowthSettingsData> {
  const locationId = useLocationStore.getState().getSelectedCoreLocationId();
  if (locationId == null) {
    throw new Error('Select a location before saving settings.');
  }

  const patches = settingsToPatchSections(payload, rawPractice);
  for (const patch of patches) {
    await growthPatchSettingsSection(patch.section, patch.body, locationId);
  }
  return fetchGrowthSettings();
}

export type SettingsOauthService = 'google_ads' | 'gsc' | 'gbp' | 'ga4';

const OAUTH_SERVICE_ALIASES: Record<string, SettingsOauthService> = {
  google_ads: 'google_ads',
  'google-ads': 'google_ads',
  gsc: 'gsc',
  gbp: 'gbp',
  ga4: 'ga4',
};

export function normalizeOauthService(service: string): SettingsOauthService | null {
  return OAUTH_SERVICE_ALIASES[service.toLowerCase()] ?? null;
}

export async function startSettingsOauth(service: SettingsOauthService) {
  const { data } = await growthPostAccepted<{ authorization_url?: string; state?: string }>(
    `/settings/connections/${service}/start-oauth/`,
  );
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('growth_oauth_service', service);
    if (data.state) sessionStorage.setItem('growth_oauth_state', String(data.state));
  }
  return { authorizationUrl: String(data.authorization_url ?? ''), state: data.state };
}

export async function completeSettingsOauth(service: SettingsOauthService, code: string, state: string) {
  return growthPost(`/settings/connections/${service}/callback/`, { code, state });
}

export async function disconnectSettingsService(service: string) {
  const normalized = normalizeOauthService(service) ?? service;
  return growthPost(`/settings/connections/${normalized}/disconnect/`);
}

export async function recomputeAvgPatientValue() {
  return growthPost('/settings/recompute-avg-patient-value/');
}
